import { createContext, useContext } from 'react';
/**
 * Context that holds a global Auth object.
 */
export const OpointAuthContext = createContext(undefined);
export const OpointAuthStateContext = createContext(undefined);
export const OpointImpersonationLoadingContext = createContext(false);
export const OpointAuthErrorContext = createContext(undefined);
/**
 * Provides the current Error state, if any. This state is updated as it changes, so it will revert to `undefined` if a new request succeeds.
 * @returns Error or undefined
 */
export function useOpointAuthError() {
    return useContext(OpointAuthErrorContext);
}
/**
 * Provides the auth state if the user is signed in. Must be inside a OpointAuthContext.Provider.
 * @returns AuthState if signed in, null if not, and undefined when waiting for the state to resolve (e.g page refreshed).
 */
export function useOpointAuthState() {
    const state = useContext(OpointAuthStateContext);
    return state;
}
/**
 * Provides the OpointUser if the user is signed in. Must either be inside a OpointAuthContext.Provider, or receive the `auth` param.
 * @returns OpointUser if signed in, null if not, and undefined when waiting for the state to resolve (e.g page refreshed).
 */
export function useOpointUser() {
    const authState = useOpointAuthState();
    if (authState === null || authState === undefined)
        return authState;
    return authState.user;
}
/**
 * Get a reference to the Auth object. Use this when making imperative calls like `await auth.getTokenString()`
 * @returns a reference to the global Auth object, if one exists.
 */
export function useOpointAuth() {
    const auth = useContext(OpointAuthContext);
    return auth;
}
/**
 * Get the current impersonation state. Similarly to useOpointAuthState, it gives undefined while it's in a pending state.
 * @returns undefined while loading (e.g right after calling `impersonate()`), null if not impersonating, or the impersonated id
 */
export function useOpointImpersonationState() {
    const loading = useContext(OpointImpersonationLoadingContext);
    const state = useOpointAuthState();
    if (state === undefined || state === null)
        return state;
    if (state && loading)
        return undefined;
    return state.impersonating ?? null;
}
//# sourceMappingURL=auth.js.map